import { IApiLink } from "@/types";
import { createStore, createEffect, combine } from "effector"
import { Operation } from "fast-json-patch"

import FinanceService from "services/FinanceService"
import FinancialFactors, { IPostFinancialFactorsDto } from "models/FinancialFactors"
import CustomError from "models/CustomError"
import { ICreateCostCategoryDto } from "models/CostCategory"
import CostCategories from "models/CostCategories"
import { ErrorCode } from "config/constants"
import OverheadFactorsSettings from "models/OverheadFactorsSettings"
import { OverheadFactorsPhase } from 'models/OverheadFactors';

// Stores
const $financialFactors = createStore<FinancialFactors | null>(null)
const $costCategories = createStore<CostCategories | null>(null)
const $overheadFactorsSaleSettings = createStore<OverheadFactorsSettings | null>(null)
const $overheadFactorsRentalSettings = createStore<OverheadFactorsSettings | null>(null)

const $financeSettings = combine(
    $financialFactors,
    $costCategories,
    $overheadFactorsSaleSettings,
    $overheadFactorsRentalSettings,
    (financialFactors, costCategories, overheadFactorsSaleSettings, overheadFactorsRentalSettings) => {
        return {
            financialFactors,
            costCategories,
            overheadFactorsSaleSettings,
            overheadFactorsRentalSettings
        }
    }
)

// Effects

const fetchFinancialFactors = createEffect(async () => {
    return FinanceService.getFinancialFactors()
})

const updateFinancialFactors = createEffect(async (newFinancialFactors: IPostFinancialFactorsDto) => {
    const financialFactors = $financialFactors.getState()

    if (!financialFactors?.linkUpdate) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }
    await FinanceService.updateFinancialFactors(newFinancialFactors, financialFactors.linkUpdate, financialFactors.eTag)
    await fetchFinancialFactors()
})

const addImputedInterestRatePeriod = createEffect(async (validFrom: string) => {
    const financialFactors = $financialFactors.getState()
    if (!financialFactors?.linkAddPeriod || !validFrom) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }
    await FinanceService.addImputedInterestRatePeriod({ validFrom }, financialFactors.linkAddPeriod)
    return fetchFinancialFactors()
})

const fetchCostCategories = createEffect(async () => {
    return FinanceService.getCostCategories()
})

const createCostCategory = createEffect(async (newCostCategoryDto: ICreateCostCategoryDto) => {
    const financeSettings = $financeSettings.getState()

    if (!financeSettings?.costCategories?.linkCreate) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    await FinanceService.createCostCategory(financeSettings.costCategories.linkCreate, newCostCategoryDto)
    await fetchCostCategories()
})

const updateCostCategory = createEffect(async ({ link, patch }: { link: IApiLink, patch: Operation[] }) => {

    if (patch.length === 0) return

    await FinanceService.updateCostCategory(link, patch)
    await fetchCostCategories()
})

const fetchOverheadFactorsSaleSettings = createEffect(async () => {
    return FinanceService.getOverheadFactorsSettings(OverheadFactorsPhase.Sale)
})
const fetchOverheadFactorsRentalSettings = createEffect(async () => {
    return FinanceService.getOverheadFactorsSettings(OverheadFactorsPhase.Rental)
})

const fetchFinanceSettings = createEffect(async () => {
    await Promise.all([
        fetchFinancialFactors(),
        fetchCostCategories(),
        fetchOverheadFactorsSaleSettings(),
        fetchOverheadFactorsRentalSettings(),
    ])
})


// Listeners
$financialFactors.on(fetchFinancialFactors.doneData, (_, payload) => payload)
$costCategories.on(fetchCostCategories.doneData, (_, payload) => payload)
$overheadFactorsSaleSettings.on(fetchOverheadFactorsSaleSettings.doneData, (_, payload) => payload)
$overheadFactorsRentalSettings.on(fetchOverheadFactorsRentalSettings.doneData, (_, payload) => payload)

// Exports
export default {
    $financeSettings,
    fetchFinanceSettings,
    fetchCostCategories,
    createCostCategory,
    updateCostCategory,
    fetchFinancialFactors,
    updateFinancialFactors,
    addImputedInterestRatePeriod,
    fetchOverheadFactorsSaleSettings,
    fetchOverheadFactorsRentalSettings
}
